import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useId, type KeyboardEvent } from 'react';

import { RECRUITMENT_METHOD } from './linkCopy';

const ARROWS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];

interface VolunteerModeSelectorProps {
  /** The volunteer link's mode from the read; EXTERNAL until the native form exists. */
  value: string | null;
  /** A refusal of the mode, shown under the cards and read with the group (P9). */
  error?: string | null;
  /** A read-only viewer: External is shown chosen, and marked disabled (P20). */
  disabled?: boolean;
}

/**
 * The Recruitment Method choice of wireframe p.5: two cards in a radiogroup.
 * Custom In-App Form is drawn but not offered until the native form arrives
 * (Day 69), so it is disabled, says "Available soon", and the arrow keys pass
 * it by; every save sends EXTERNAL (P9, P23).
 */
export function VolunteerModeSelector({
  value,
  error = null,
  disabled = false,
}: VolunteerModeSelectorProps) {
  const headingId = useId();
  const errorId = useId();
  const native = value === 'NATIVE';

  // The only card that can be chosen is External, so an arrow key has
  // nowhere to go: it stays put rather than landing on the disabled card.
  const stay = (event: KeyboardEvent) => {
    if (ARROWS.includes(event.key)) event.preventDefault();
  };

  return (
    <Box component="section" sx={{ mb: 3 }}>
      <Typography id={headingId} variant="h6" component="h2" sx={{ fontWeight: 600 }}>
        {RECRUITMENT_METHOD.title}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {RECRUITMENT_METHOD.description}
      </Typography>
      <Box
        role="radiogroup"
        aria-labelledby={headingId}
        aria-describedby={error ? errorId : undefined}
        onKeyDown={stay}
        data-testid="arc-volunteer-mode-group"
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
        }}
      >
        <ModeCard
          title={RECRUITMENT_METHOD.native.title}
          description={RECRUITMENT_METHOD.native.description}
          note={RECRUITMENT_METHOD.availableSoon}
          checked={native}
          disabled
          focusable={false}
          testId="arc-volunteer-mode-native-radio"
        />
        <ModeCard
          title={RECRUITMENT_METHOD.external.title}
          description={RECRUITMENT_METHOD.external.description}
          checked={!native}
          disabled={disabled}
          focusable
          testId="arc-volunteer-mode-external-radio"
        />
      </Box>
      {error ? (
        <Typography
          id={errorId}
          role="alert"
          color="error"
          variant="body2"
          sx={{ mt: 1 }}
          data-testid="arc-volunteer-mode-error"
        >
          {error}
        </Typography>
      ) : null}
    </Box>
  );
}

interface ModeCardProps {
  title: string;
  description: string;
  /** A short state under the description, such as "Available soon". */
  note?: string;
  checked: boolean;
  disabled: boolean;
  /** In the Tab order: the chosen, usable card only. */
  focusable: boolean;
  testId: string;
}

/** One card: named by its title, described by its line and its note. */
function ModeCard({
  title,
  description,
  note,
  checked,
  disabled,
  focusable,
  testId,
}: ModeCardProps) {
  const titleId = useId();
  const descriptionId = useId();
  const noteId = useId();

  return (
    <Paper
      variant="outlined"
      role="radio"
      aria-checked={checked}
      aria-disabled={disabled || undefined}
      aria-labelledby={titleId}
      aria-describedby={note ? `${descriptionId} ${noteId}` : descriptionId}
      tabIndex={focusable ? 0 : -1}
      data-testid={testId}
      sx={{
        p: 2,
        borderWidth: checked ? 2 : 1,
        borderColor: checked ? 'primary.main' : 'divider',
        opacity: disabled && !checked ? 0.6 : 1,
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
      }}
    >
      <Typography id={titleId} sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography id={descriptionId} variant="body2" color="text.secondary">
        {description}
      </Typography>
      {note ? <Chip id={noteId} size="small" label={note} sx={{ mt: 1 }} /> : null}
    </Paper>
  );
}
